import React, { useState, useEffect, useMemo } from "react";
import { useAdminStore } from "../stores/adminStore";
import { DeleteButton, useConfirmationStore } from "components";

// Функція для перевірки, чи змінились дозволи
const arePermissionsModified = (originalRole, currentPermissionsMap) => {
  if (!originalRole || !originalRole.permissions) {
    return false;
  }

  const originalPermissionIds = new Set(originalRole.permissions.map((p) => p.id));
  const currentPermissionIds = new Set(currentPermissionsMap.keys());

  if (originalPermissionIds.size !== currentPermissionIds.size) {
    return true;
  }

  for (const id of originalPermissionIds) {
    if (!currentPermissionIds.has(id)) {
      return true;
    }
  }

  return false;
};

// Компонент керування ролями та їх дозволами сторінки "Панель адміністратора"
const RolesManagement = () => {
  // Достаємо дані з глобального стору
  const roles = useAdminStore((state) => state.roles);
  const permissions = useAdminStore((state) => state.permissions);
  // Достаємо методи з глобального стору
  const { updateRolePermissions, createRole, deleteRole } = useAdminStore.getState();
  // Достаємо методи зі стору підтвердження
  const { showConfirmation } = useConfirmationStore.getState();

  // Локальний стан для збереження ID обраної ролі
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  // Локальний стан для збереження дозволів ролі
  const [rolePermissions, setRolePermissions] = useState(new Map());
  // Локальний стан для визначення моменту переключення ролей
  const [isSwitchingRole, setIsSwitchingRole] = useState(false);
  // Локальний стан для відстеження на який дозвіл наведено мишку
  const [hoveredPermissionId, setHoveredPermissionId] = useState(null);
  // Локальний стан для відстеження позиції тултипу
  const [tooltipPosition, setTooltipPosition] = useState("top");

  // Локальний стан для відстеження режиму редагування
  const [mode, setMode] = useState("placeholder");
  // Локальний стан для збереження даних нової ролі
  const [newRoleData, setNewRoleData] = useState({ name: "", description: "" });

  // Ефект оновлює список дозволів, коли змінюється обрана роль
  useEffect(() => {
    // Знаходимо повний об'єкт обраної ролі
    const selectedRole = roles.find((role) => role.id === selectedRoleId);

    if (selectedRole && selectedRole.permissions) {
      // Створюємо новий Map з поточними дозволами ролі
      const permissionsMap = new Map();
      selectedRole.permissions.forEach((p) => permissionsMap.set(p.id, true));
      setRolePermissions(permissionsMap);
    } else {
      // Якщо роль не обрана або не має дозволів, очищуємо Map
      setRolePermissions(new Map());
    }

    setIsSwitchingRole(false);
  }, [selectedRoleId, roles]);

  // Перевірка, чи змінились дозволи
  const hasChanges = useMemo(() => {
    if (isSwitchingRole) {
      return false;
    }

    const originalRole = roles.find((role) => role.id === selectedRoleId);
    return arePermissionsModified(originalRole, rolePermissions);
  }, [selectedRoleId, roles, rolePermissions, isSwitchingRole]);

  // Обробник вибору ролі для редагування
  const handleRoleSelect = (roleId) => {
    // Якщо обираємо ту саму роль, нічого не робимо
    if (roleId === selectedRoleId) return;

    setIsSwitchingRole(true);
    setSelectedRoleId(roleId);
    setMode("editing"); // Переключаємо режим на редагування
  };

  // Обробник переходу в режим створення ролі
  const handleShowCreateForm = () => {
    setSelectedRoleId(null); // Скидаємо виділення з існуючих ролей
    setNewRoleData({ name: "", description: "" }); // Очищуємо поля форми
    setMode("creating"); // Переключаємо режим на створення
  };

  // Обробник створення ролі
  const handleCreateRole = async () => {
    if (!newRoleData.name.trim()) {
      return;
    }
    try {
      await createRole(newRoleData);
      setMode("placeholder");
    } catch (err) {}
  };

  const handleDeleteRole = async () => {
    if (!selectedRoleId) return;

    // Знаходимо назву ролі для підтвердження
    const roleName = roles.find((role) => role.id === selectedRoleId)?.name;

    showConfirmation({
      title: "Підтвердити видалення",
      message: `Ви впевнені, що хочете видалити роль "${roleName}"? Всі користувачі з цією роллю отримають роль "Без прав".`,
      onConfirm: async () => {
        try {
          await deleteRole(selectedRoleId);
          setSelectedRoleId(null);
          setMode("placeholder");
        } catch (err) {}
      },
    });
  };

  // Обробник зміни стану чекбокса (дозволу)
  const handlePermissionChange = (permissionId) => {
    // Створюємо копію Map, щоб уникнути прямої мутації стану
    const newPermissions = new Map(rolePermissions);
    if (newPermissions.has(permissionId)) {
      newPermissions.delete(permissionId); // Якщо дозвіл був, видаляємо
    } else {
      newPermissions.set(permissionId, true); // Якщо не було, додаємо
    }
    setRolePermissions(newPermissions);
  };

  // Обробник збереження змін
  const handleSave = async () => {
    if (!hasChanges || selectedRoleId === null) return;

    const permissionIds = Array.from(rolePermissions.keys());
    await updateRolePermissions(selectedRoleId, permissionIds);
  };

  // Обробник наведення мишки на дозвіл
  const handleMouseEnter = (e, permissionId) => {
    const itemElement = e.currentTarget;
    const rect = itemElement.getBoundingClientRect();
    const TOOLTIP_ESTIMATED_HEIGHT = 160;

    // Якщо зверху місця немає, показуємо знизу
    if (rect.top < TOOLTIP_ESTIMATED_HEIGHT) {
      setTooltipPosition("bottom");
    } else {
      setTooltipPosition("top");
    }

    setHoveredPermissionId(permissionId);
  };

  return (
    <div className="roles-management-container">
      <div className="roles-list">
        <h5 className="roles-title">Ролі</h5>
        {roles.map((role) => (
          <button
            key={role.id}
            className={`role-item ${selectedRoleId === role.id ? "selected" : ""}`}
            onClick={() => handleRoleSelect(role.id)}
          >
            {role.name}
          </button>
        ))}
        <button className="role-item add-new" onClick={handleShowCreateForm}>
          + Нова роль
        </button>
      </div>

      <div className="permissions-editor">
        {mode === "placeholder" && (
          <p className="placeholder">Оберіть роль для редагування або створіть нову</p>
        )}

        {mode === "editing" && selectedRoleId !== null && (
          <>
            <div className="permissions-list">
              {permissions.map((permission) => (
                <div
                  key={permission.id}
                  className="permission-item"
                  onMouseEnter={(e) => handleMouseEnter(e, permission.id)}
                  onMouseLeave={() => setHoveredPermissionId(null)}
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={rolePermissions.has(permission.id)}
                      onChange={() => handlePermissionChange(permission.id)}
                    />
                    {permission.name}
                  </label>
                  {hoveredPermissionId === permission.id && permission.description && (
                    <div
                      className={`permission-item-tooltip permission-item-tooltip--${tooltipPosition}`}
                    >
                      {permission.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="role-actions">
              <DeleteButton onDelete={handleDeleteRole} />
              <button
                className="save-button focus-button"
                onClick={handleSave}
                disabled={!hasChanges}
              >
                Зберегти зміни
              </button>
            </div>
          </>
        )}

        {mode === "creating" && (
          <div className="create-role-form">
            <div className="form-group">
              <label htmlFor="role-name">Назва ролі</label>
              <input
                id="role-name"
                type="text"
                value={newRoleData.name}
                onChange={(e) =>
                  setNewRoleData({ ...newRoleData, name: e.target.value })
                }
                placeholder="Назва нової ролі"
              />
            </div>
            <div className="form-group">
              <label htmlFor="role-description">Опис (необов'язково)</label>
              <textarea
                id="role-description"
                value={newRoleData.description}
                onChange={(e) =>
                  setNewRoleData({ ...newRoleData, description: e.target.value })
                }
                placeholder="Основні права та обов'язки..."
                rows="8"
              />
            </div>
            <button
              className="save-button focus-button"
              onClick={handleCreateRole}
              disabled={!newRoleData.name.trim()}
            >
              Створити
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesManagement;
